import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdDeleteOutline, MdEditNote } from "react-icons/md";
import styles from "./Dashboard.module.scss";
import useCloudinary from "../../utils/useCloudinary";
import {
  getMembers,
  createMember,
  updateMember,
  deleteMember,
} from "../../utils/network/Crud";

const emptyForm = { name: "", designation: "", important: false, image: "" };

const ManageMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { uploadImage, uploading } = useCloudinary();

  const loadMembers = async () => {
    setLoading(true);
    const result = await getMembers();
    setMembers(result?.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    setShowModal(true);
  };

  const openEdit = (member) => {
    setEditingId(member.id);
    setForm({
      name: member.name || "",
      designation: member.designation || "",
      important: !!member.important,
      image: member.image || "",
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.designation) {
      toast.error("Name and designation are required");
      return;
    }
    try {
      let imageUrl = form.image;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      if (!imageUrl) {
        toast.error("Please select an image");
        return;
      }
      if (editingId) {
        await updateMember(editingId, { ...form, image: imageUrl });
        toast.success("Member updated");
      } else {
        await createMember({ ...form, image: imageUrl });
        toast.success("Member added");
      }
      setShowModal(false);
      loadMembers();
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMember(deleteId);
      toast.success("Member removed");
      setDeleteId(null);
      loadMembers();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Members</h2>
          <p>Manage committee members shown on the Community page.</p>
        </div>
        <button className={styles.primaryBtn} onClick={openAdd}>
          <MdAdd /> Add Member
        </button>
      </div>

      <div className={styles.panel}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Designation</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Loading…</td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={5}>No members added yet.</td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <img src={member.image} alt={member.name} className={styles.thumb} />
                    </td>
                    <td>{member.name}</td>
                    <td>{member.designation}</td>
                    <td>
                      <span className={member.important ? styles.badgeGold : styles.badge}>
                        {member.important ? "Committee" : "Member"}
                      </span>
                    </td>
                    <td>
                      <button className={styles.iconBtn} onClick={() => openEdit(member)}>
                        <MdEditNote />
                      </button>
                      <button
                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                        onClick={() => setDeleteId(member.id)}
                      >
                        <MdDeleteOutline />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Member" : "Add Member"}</h3>
            <form className={styles.formGrid} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Designation</label>
                <input
                  value={form.designation}
                  onChange={(e) => setForm({ ...form, designation: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                />
                {form.image && !imageFile && (
                  <img src={form.image} alt="current" className={styles.thumb} style={{ marginTop: 6 }} />
                )}
              </div>
              <label className={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={form.important}
                  onChange={(e) => setForm({ ...form, important: e.target.checked })}
                />
                Show in Committee / Important section
              </label>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={uploading}>
                  {uploading ? "Saving…" : editingId ? "Save Changes" : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className={styles.modalOverlay} onClick={() => setDeleteId(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3>Remove Member?</h3>
            <p style={{ color: "#b6acc9", fontSize: 13 }}>This action cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.secondaryBtn} onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button className={`${styles.primaryBtn} ${styles.dangerBtn}`} onClick={handleDelete}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageMembers;
